/**
 * The "ready anchor" HUD button — a one-tap Drop-anchor control that rides on the
 * chart the same way the MOB button does, so the hook can be dropped at the exact
 * moment it hits the bottom without opening the menu.
 *
 * It only shows when the skipper has armed "ready anchor" from the AnchorWatch
 * control AND the anchor isn't already down. A tap drops the anchor at the
 * vessel's current position and arms the watch, reusing the anchor store's
 * dropAnchor (the same action the AnchorWatch control's "Drop here" uses), so
 * there's one definition of what dropping the anchor means. Once dropped, the
 * anchor is set, so the button hides itself; it comes back if the anchor is later
 * weighed while "ready anchor" is still on.
 *
 * Unlike MOB this isn't a red pulsing/confirm control, but dropping the hook and
 * arming the watch is a consequential act, so it gets the firmer `commit` buzz
 * (not a light tap) — you should feel that the anchor's away without looking.
 * Being a plain child of the control stack, an open menu or sheet covers it
 * rather than it floating on top.
 */
import { anchorPositionNow } from '@/ownship/anchorDrop';
import { useAnchorWatchStore } from '@/store/anchorWatch';
import { useChartPlotterStore } from '@/store';
import { useShellStore } from '@/store/shell';
import { haptic } from '@/ui/haptics';
import { AnchorIcon } from './icons';

export function AnchorDropButton() {
  const armed = useShellStore((s) => s.anchorDropArmed);
  const anchor = useAnchorWatchStore((s) => s.anchor);
  const dropAnchor = useAnchorWatchStore((s) => s.dropAnchor);
  const ownPos = useChartPlotterStore((s) => s.ownship.position);
  const showToast = useShellStore((s) => s.showToast);

  // Off the HUD unless armed, and never while the hook is already down (nothing
  // to drop). Weighing the anchor brings it back if "ready anchor" is still on.
  if (!armed || anchor != null) return null;

  const onDrop = () => {
    if (!ownPos) {
      showToast('Anchor — no vessel position yet');
      return;
    }
    haptic('commit');
    dropAnchor(anchorPositionNow(ownPos) ?? ownPos);
    showToast('Anchor dropped at the boat — watch armed');
  };

  return (
    <button
      type="button"
      className="acp-ctrl acp-anchor-ctrl"
      aria-label="Drop anchor at the vessel and arm the anchor watch"
      title="Drop anchor at the vessel and arm the anchor watch"
      onClick={onDrop}
    >
      <AnchorIcon />
    </button>
  );
}
